/**
 * Result of a report download: the PDF blob together with the file name
 * suggested by the backend (parsed from the Content-Disposition header, with a
 * sensible fallback).
 */
export interface ReportDownload {
  blob: Blob;
  fileName: string;
}

/**
 * Extracts the file name from a Content-Disposition header value.
 * Supports both the RFC 5987 `filename*=UTF-8''...` form and the plain
 * `filename="..."` form. Returns the fallback when none is present.
 *
 * @param {string | undefined} contentDisposition raw header value
 * @param {string} fallback file name used when the header carries none
 * @return {*} {string} the file name to offer the user
 */
export const parseReportFileName = (
  contentDisposition: string | undefined,
  fallback: string,
): string => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = /filename\*=utf-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return fallback;
};

/**
 * Wraps raw binary response data in a PDF blob. The generated API clients type
 * their PDF responses as `void`, so the payload has to be cast before it can be
 * handed to the browser.
 *
 * @param {unknown} data raw response body, requested with `responseType: 'blob'`
 * @return {*} {Blob} the response as an `application/pdf` blob
 */
export const toPdfBlob = (data: unknown): Blob =>
  new Blob([data as BlobPart], { type: 'application/pdf' });
